import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  getLoyaltyProgram, createLoyaltyProgram, updateLoyaltyProgram,
  getLoyaltyRewards, createLoyaltyReward, updateLoyaltyReward, deleteLoyaltyReward,
  getLoyaltyClientPoints, redeemLoyaltyReward,
} from '../../services/api';
import { useConfirm } from '../../components/ConfirmDialog';
import MoneyInput from '../../components/MoneyInput';
import PercentInput from '../../components/PercentInput';

const REWARD_TYPES = [
  { value: 'discount',     label: 'Desconto (R$)' },
  { value: 'discount_pct', label: 'Desconto (%)' },
  { value: 'free_service', label: 'Serviço grátis' },
  { value: 'other',        label: 'Outro' },
];

export default function TenantLoyalty() {
  const confirm = useConfirm();
  const [program, setProgram] = useState(null);
  const [rewards, setRewards] = useState([]);
  const [tab,     setTab]     = useState('program'); // program | rewards | lookup
  const [loading, setLoading] = useState(true);
  const [modal,   setModal]   = useState(null);
  const [saving,  setSaving]  = useState(false);
  const [progForm, setProgForm] = useState({ name: '', points_per_appointment: 10, points_per_currency: 0, active: true });
  const [rwdForm,  setRwdForm]  = useState({ name: '', description: '', points_required: 100, reward_type: 'discount', reward_value: 0, active: true });
  const [lookup,   setLookup]   = useState({ phone: '', result: null });

  const load = useCallback(() => {
    setLoading(true);
    getLoyaltyProgram()
      .then(r => { setProgram(r.data.program); setRewards(r.data.rewards || []); if (r.data.program) setProgForm(p => ({ ...p, name: r.data.program.name, points_per_appointment: r.data.program.points_per_appointment, points_per_currency: r.data.program.points_per_currency, active: !!r.data.program.active })); })
      .catch(() => toast.error('Erro ao carregar programa'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const onSaveProgram = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (program) { await updateLoyaltyProgram(progForm); toast.success('Programa atualizado'); }
      else { await createLoyaltyProgram(progForm); toast.success('Programa criado'); }
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const onSaveReward = async e => {
    e.preventDefault();
    setSaving(true);
    try {
      if (modal === 'create-reward') { await createLoyaltyReward(rwdForm); toast.success('Recompensa criada'); }
      else { await updateLoyaltyReward(modal.id, rwdForm); toast.success('Recompensa atualizada'); }
      setModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.error || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };

  const onDeleteReward = async id => {
    const ok = await confirm({
      title: 'Remover recompensa?',
      message: 'A recompensa não estará mais disponível para resgate. Clientes que já resgataram no passado mantêm o histórico.',
      confirmText: 'Remover',
      variant: 'danger',
    });
    if (!ok) return;
    try { await deleteLoyaltyReward(id); toast.success('Removida'); load(); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro'); }
  };

  const onLookup = async e => {
    e.preventDefault();
    try { const r = await getLoyaltyClientPoints(lookup.phone); setLookup(l => ({ ...l, result: r.data })); }
    catch (err) { toast.error(err.response?.data?.error || 'Erro'); }
  };

  const tabStyle = active => ({
    padding: '8px 18px', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
    background: active ? 'var(--primary)' : 'transparent',
    color: active ? '#fff' : 'var(--gray-600)',
    border: 'none',
  });

  return (
    <div className="page-body">
      <h1 style={{ margin: '0 0 20px', color: 'var(--gray-900)', fontSize: 22, fontWeight: 700 }}>Programa de Fidelidade</h1>

      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--gray-100)', padding: 4, borderRadius: 10, width: 'fit-content' }}>
        <button style={tabStyle(tab === 'program')} onClick={() => setTab('program')}>Configurações</button>
        <button style={tabStyle(tab === 'rewards')} onClick={() => setTab('rewards')}>Recompensas</button>
        <button style={tabStyle(tab === 'lookup')} onClick={() => setTab('lookup')}>Consultar pontos</button>
      </div>

      {loading ? <div style={{ textAlign: 'center', padding: 60 }}><div className="spinner" /></div> : (
        <>
          {tab === 'program' && (
            <div className="card" style={{ maxWidth: 500, padding: 28 }}>
              <h3 style={{ margin: '0 0 20px', color: 'var(--gray-800)' }}>{program ? 'Editar programa' : 'Criar programa de fidelidade'}</h3>
              <form onSubmit={onSaveProgram}>
                <div className="form-group"><label className="label">Nome do programa *</label>
                  <input className="input" value={progForm.name} onChange={e => setProgForm(f => ({ ...f, name: e.target.value }))} required placeholder="Ex: Clube Fidelidade" />
                </div>
                <div className="form-group"><label className="label">Pontos por agendamento concluído</label>
                  <input className="input" type="number" value={progForm.points_per_appointment} onChange={e => setProgForm(f => ({ ...f, points_per_appointment: parseInt(e.target.value) || 0 }))} />
                </div>
                <div className="form-group"><label className="label">Pontos por R$ gasto (0 = desativado)</label>
                  <input className="input" type="number" step="0.1" value={progForm.points_per_currency} onChange={e => setProgForm(f => ({ ...f, points_per_currency: parseFloat(e.target.value) || 0 }))} />
                </div>
                {program && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
                    <input type="checkbox" id="pactive" checked={progForm.active} onChange={e => setProgForm(f => ({ ...f, active: e.target.checked }))} />
                    <label htmlFor="pactive" style={{ fontSize: 14, cursor: 'pointer' }}>Programa ativo</label>
                  </div>
                )}
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>{saving ? '' : program ? 'Salvar alterações' : 'Criar programa'}</button>
              </form>
            </div>
          )}

          {tab === 'rewards' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
                <button className="btn btn-primary" onClick={() => { setRwdForm({ name: '', description: '', points_required: 100, reward_type: 'discount', reward_value: 0, active: true }); setModal('create-reward'); }}>+ Nova recompensa</button>
              </div>
              {!program ? (
                <div className="empty-state"><h3>Configure o programa primeiro</h3></div>
              ) : rewards.length === 0 ? (
                <div className="empty-state"><div style={{ fontSize: 40, marginBottom: 12 }}>🎁</div><h3>Nenhuma recompensa configurada</h3><p>Adicione recompensas que os clientes podem resgatar com seus pontos.</p></div>
              ) : rewards.map(r => (
                <div key={r.id} className="card" style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 16, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: 'var(--gray-900)' }}>{r.name}</div>
                    <div style={{ fontSize: 13, color: 'var(--gray-500)', marginTop: 2 }}>
                      {r.points_required} pontos · {REWARD_TYPES.find(t => t.value === r.reward_type)?.label} {r.reward_value > 0 ? `(${r.reward_value})` : ''}
                    </div>
                    {r.description && <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>{r.description}</div>}
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button className="btn btn-secondary" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => { setRwdForm({ name: r.name, description: r.description || '', points_required: r.points_required, reward_type: r.reward_type, reward_value: r.reward_value, active: !!r.active }); setModal(r); }}>Editar</button>
                    <button className="btn btn-danger" style={{ padding: '4px 10px', fontSize: 12 }} onClick={() => onDeleteReward(r.id)}>Remover</button>
                  </div>
                </div>
              ))}
            </>
          )}

          {tab === 'lookup' && (
            <div className="card" style={{ maxWidth: 460, padding: 28 }}>
              <h3 style={{ margin: '0 0 16px', color: 'var(--gray-800)' }}>Consultar pontos do cliente</h3>
              <form onSubmit={onLookup} style={{ display: 'flex', gap: 10, marginBottom: 20 }}>
                <input className="input" placeholder="Telefone do cliente" value={lookup.phone} onChange={e => setLookup(l => ({ ...l, phone: e.target.value, result: null }))} required />
                <button type="submit" className="btn btn-primary" style={{ whiteSpace: 'nowrap' }}>Consultar</button>
              </form>
              {lookup.result && (
                <div>
                  <div style={{ fontSize: 36, fontWeight: 800, color: 'var(--primary)', marginBottom: 4 }}>{lookup.result.points}</div>
                  <div style={{ fontSize: 14, color: 'var(--gray-500)', marginBottom: 20 }}>pontos acumulados</div>
                  {lookup.result.history.length > 0 && (
                    <>
                      <h4 style={{ margin: '0 0 8px', fontSize: 13, color: 'var(--gray-600)' }}>Histórico</h4>
                      {lookup.result.history.map(h => (
                        <div key={h.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid var(--gray-100)', fontSize: 13 }}>
                          <span>{h.notes || h.type}</span>
                          <span style={{ fontWeight: 700, color: h.points > 0 ? '#10b981' : '#ef4444' }}>{h.points > 0 ? '+' : ''}{h.points}</span>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {modal !== null && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">{modal === 'create-reward' ? 'Nova recompensa' : 'Editar recompensa'}</h3>
              <button className="modal-close" onClick={() => setModal(null)}>✕</button>
            </div>
            <form onSubmit={onSaveReward}>
              <div className="modal-body">
                <div className="form-group"><label className="label">Nome *</label>
                  <input className="input" value={rwdForm.name} onChange={e => setRwdForm(f => ({ ...f, name: e.target.value }))} required />
                </div>
                <div className="form-group"><label className="label">Descrição</label>
                  <input className="input" value={rwdForm.description} onChange={e => setRwdForm(f => ({ ...f, description: e.target.value }))} />
                </div>
                <div className="form-group"><label className="label">Pontos necessários *</label>
                  <input className="input" type="number" value={rwdForm.points_required} onChange={e => setRwdForm(f => ({ ...f, points_required: parseInt(e.target.value) || 0 }))} required />
                </div>
                <div className="form-group"><label className="label">Tipo de recompensa</label>
                  <select className="input" value={rwdForm.reward_type} onChange={e => setRwdForm(f => ({ ...f, reward_type: e.target.value }))}>
                    {REWARD_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="label">
                    {rwdForm.reward_type === 'discount_pct' ? 'Porcentagem (%)' : 'Valor (R$)'}
                  </label>
                  {rwdForm.reward_type === 'discount_pct' ? (
                    <PercentInput
                      className="input"
                      value={rwdForm.reward_value}
                      onChange={v => setRwdForm(f => ({ ...f, reward_value: v }))}
                      min={0}
                      max={100}
                    />
                  ) : (
                    <MoneyInput
                      className="input"
                      value={rwdForm.reward_value}
                      onChange={v => setRwdForm(f => ({ ...f, reward_value: v }))}
                      min={0}
                    />
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setModal(null)}>Cancelar</button>
                <button type="submit" className={`btn btn-primary${saving ? ' btn-loading' : ''}`} disabled={saving}>{saving ? '' : 'Salvar'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
